import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../../../../../helpers';
import {
    IExpense,
    IS_DRAFT,
    RECONCILIATION_ONLY,
    REQUIRE_EXTERNAL_CODE,
    REQUIRE_SUPPLEMENT,
} from './expense-form';
import { useExpenseReceiptTypeIdInputQuery } from '../../../../../../services';
import { useSetExpenseTaxRequirement } from './use-set-expense-tax-requirement';

const ACCOUNT: keyof IExpense = 'account';
const ID: keyof IExpense = 'id';
const DATE: keyof IExpense = 'date';
const RECEIPT_TYPE_ID: keyof IExpense = 'receipt_type_id';
const EXPECTED_PAYMENT_DATE: keyof IExpense = 'expected_payment_date';

// Pre-fills expense fields from the selected supplier account's defaults.
// Credit days derive expected_payment_date only while creating; edits preserve
// the loaded date and only re-derive receipt-aware document requirements.
export function usePrefillFromSupplierAccount() {
    const rx = useFormContext();
    const { data } = useExpenseReceiptTypeIdInputQuery();
    const { setExpenseTaxRequirement } = useSetExpenseTaxRequirement();

    // A non-taxable receipt cannot carry a complemento, so the supplier default
    // must not turn the requirement back on.
    const receiptAppliesTax = (whenUnset = true) => {
        const receiptTypeId = rx.getValues(
            RECEIPT_TYPE_ID,
        ) as IExpense['receipt_type_id'];
        const receiptType = (data?.getReceiptTypes ?? []).find(
            (type) => type.id === receiptTypeId,
        );
        return receiptType ? receiptType.applies_tax : whenUnset;
    };

    const setPaymentDate = (
        account: IExpense['account'],
        date?: IExpense['date'],
    ) => {
        if (rx.getValues(ID) || !account || !date) return;
        const creditDays = Number(account.supplier_credit_days ?? 0);
        if (creditDays === 0) return;
        rx.setValue(
            EXPECTED_PAYMENT_DATE,
            dayjs(date).add(creditDays, 'day').format(DATE_FORMAT),
        );
    };

    const prefillFromAccount = (account: IExpense['account']) => {
        if (!account) {
            rx.setValue(REQUIRE_EXTERNAL_CODE, false);
            setExpenseTaxRequirement(false);
            return;
        }
        // Seed this expense's reconciliation_only from the supplier account's
        // default. Only runs on an explicit account pick (this handler is the
        // autocomplete's onChange), so it never clobbers a saved value on open.
        rx.setValue(
            RECONCILIATION_ONLY,
            !!account.supplier_reconciliation_only,
        );
        // Same pattern for is_draft: seed from the supplier account default
        // (monitored suppliers start ON/draft).
        rx.setValue(IS_DRAFT, !!account.supplier_is_draft);
        rx.setValue(
            REQUIRE_SUPPLEMENT,
            receiptAppliesTax() && !!account.supplier_require_supplement,
        );
        rx.setValue(
            REQUIRE_EXTERNAL_CODE,
            receiptAppliesTax(false) &&
                !!account.supplier_requires_external_code,
        );
        setExpenseTaxRequirement(
            receiptAppliesTax(false) && !!account.supplier_requires_tax,
        );
        setPaymentDate(account, rx.getValues(DATE) as IExpense['date']);
    };

    const recomputePaymentDate = (date?: IExpense['date']) => {
        setPaymentDate(rx.getValues(ACCOUNT) as IExpense['account'], date);
    };

    return { prefillFromAccount, recomputePaymentDate };
}
